import { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import ServiceCollection from 'src/services/ServiceCollection';
import CreateMultiAllocationWorksheetRequest from 'src/models/multidimensional-allocation/CreateMultiAllocationWorksheetRequest';

/**
 * Custom hook for managing worksheet creation functionality
 * Encapsulates modal state, creation logic, and navigation
 * 
 * @param services - Service collection for API operations
 */
export const useWorksheetCreation = (services: ServiceCollection) => {
    const history = useHistory();
    
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [createWorksheetRequest, setCreateWorksheetRequest] = useState<CreateMultiAllocationWorksheetRequest | null>(null);

    // Use the create worksheet service hook
    const [createResult, createLoading, createError] = services.multidimensionalAllocationService.createWorksheet(createWorksheetRequest);

    // Handle create worksheet success
    useEffect(() => {
        if (createResult) {
            // Close modal
            setIsModalVisible(false);
            setCreateWorksheetRequest(null);

            // Redirect to calculation page with worksheetId
            const worksheetId = createResult.worksheetData.worksheetId;
            history.push(`/calculations/multidimensionalAllocation/calculation/${worksheetId}`);
        }
    }, [createResult, history]);

    // Modal event handlers with useCallback for performance
    const handleCreateWorksheetClick = useCallback(() => {
        setIsModalVisible(true);
    }, []);

    const handleModalDismiss = useCallback(() => {
        setIsModalVisible(false);
        setCreateWorksheetRequest(null);
    }, []);

    const handleModalSubmit = useCallback((request: CreateMultiAllocationWorksheetRequest) => {
        setCreateWorksheetRequest(request);
    }, []);

    return {
        isModalVisible,
        createLoading,
        createError,
        handleCreateWorksheetClick,
        handleModalDismiss,
        handleModalSubmit
    };
};
